import React, { useEffect, useState } from "react";
import { Snackbar, Alert, TextField } from "@mui/material";
import { useOutletContext, useNavigate } from "react-router-dom";
import { TablePagination } from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { getAllFlexperts, updateConsultantToFlexpert } from "./Helper";
import api, { baseURL } from "../../../utils/api";
import { IconButton, Switch, Button } from "@mui/material";
import { Edit2, Trash2, Play } from "lucide-react";
import countryData from 'country-calling-code';
import FlexpertPaymentModal from "../../../components/FlexpertPaymentModal/FlexpertPaymentModal";
import { useDebounce } from "../../../hooks";
import MuiModal from "../../../components/MuiModal";

const FlexpertUserList = () => {
  const context = useOutletContext();
  const navigate = useNavigate();
  const [flexpert, setFlexpert] = useState([]);
  const [flexpertPage, setFlexpertPage] = useState(0);
  const [flexpertLimit, setFlexpertLimit] = useState(9);
  const [flexpertTotal, setFlexpertTotal] = useState(0);
  const [toggle, setToggle] = useState(true);
  const [search, setSearch] = useState('')
  const debounceSearch = useDebounce(search, 1000);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [userPermissions, setUserPermissions] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [flexpertModalOpen, setFlexpertModalOpen] = useState(false);
  const [selectedFlexpert, setSelectedFlexpert] = useState(null);
  const [isUpdatingFlexpert, setIsUpdatingFlexpert] = useState(false);
  const [consultantSearch, setConsultantSearch] = useState('');
  const [consultantOptions, setConsultantOptions] = useState([]);
  const [consultantLoading, setConsultantLoading] = useState(false);
  const [selectedConsultant, setSelectedConsultant] = useState(null);
  const debouncedConsultantSearch = useDebounce(consultantSearch, 1000);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [flexpertToRemove, setFlexpertToRemove] = useState(null);
  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [selectedVideoUrl, setSelectedVideoUrl] = useState(null);

  const handleChangePage = (event, newPage) => {
    fetchData(newPage, flexpertLimit);
    setFlexpertPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setFlexpertLimit(parseInt(event.target.value, 10));
    setFlexpertPage(0);
    fetchData(0, parseInt(event.target.value, 10));
  };

  const fetchData = (page, limit) => {
    getAllFlexperts(page, limit, debounceSearch)
      .then((data) => {
        setFlexpert(data.data.consultants.rows);
        setFlexpertTotal(data.data.consultants.totalItems);
      })
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    context(["Flexperts", "List of Flexperts"]);
    getAllFlexperts(flexpertPage, flexpertLimit, debounceSearch)
      .then((data) => {
        setFlexpert(data.data.consultants.rows);
        setFlexpertTotal(data.data.consultants.totalItems);
      })
      .catch((err) => console.log(err));
  }, [toggle, debounceSearch]);

  // Fetch consultants for autocomplete
  useEffect(() => {
    if (debouncedConsultantSearch && debouncedConsultantSearch.length >= 2) {
      setConsultantLoading(true);
      api.get(`${baseURL}/user/allconsultants?search=${debouncedConsultantSearch}&page=0&size=50&flexExpert=false`)
        .then((data) => {
          const consultants = data?.data?.consultants?.rows || [];
          console.log('Consultants fetched:', consultants); // Debug log
          setConsultantOptions(consultants);
          setConsultantLoading(false);
        })
        .catch((err) => {
          console.error(err);
          setConsultantOptions([]);
          setConsultantLoading(false);
        });
    } else {
      setConsultantOptions([]);
    }
  }, [debouncedConsultantSearch]);

  useEffect(() => {
    const cmsPermissions = localStorage.getItem('CMSUsersPermission');
    if (cmsPermissions) {
      const permissions = JSON.parse(cmsPermissions);
      setUserPermissions(permissions);
      setIsAdmin(false);
      // Redirect if user doesn't have 'user-manager' permission
      if (!permissions.includes('user-manager')) {
        navigate('/Dashboard');
      }
    } else {
      setUserPermissions([]);
      setIsAdmin(true);
    }
  }, [navigate]);

  const handleDeleteFlexpert = (flexpert) => {
    setFlexpertToRemove(flexpert);
    setConfirmModalOpen(true);
  };

  const handleVerificationToggle = (flexpert, checked) => {
    const verifiedByAdmin = checked;
    updateFlexpertVerification(flexpert.id, verifiedByAdmin);
  };

  const handleViewVideo = (videoUrl) => {
    setSelectedVideoUrl(videoUrl);
    setVideoModalOpen(true);
  };

  const handleShowInterviewToggle = (flexpert, checked) => {
    const showInterview = checked;
    updateShowInterview(flexpert.id, showInterview);
  };

  const handleConfirmRemoveFlexpert = () => {
    if (flexpertToRemove) {
      setFlexpert(prevFlexperts => 
        prevFlexperts.map(flex => 
          flex.id === flexpertToRemove.id 
            ? { ...flex, flexpert: false } 
            : flex
        )
      );
      updateFlexpertStatus(flexpertToRemove.id, false, []);
      setConfirmModalOpen(false);
      setFlexpertToRemove(null);
    }
  };

  const handleEditFlexpert = (flexpert) => {
    setSelectedFlexpert(flexpert);
    setFlexpertModalOpen(true);
  };

  const handleFlexpertSubmit = (formData) => {
    if (!selectedFlexpert) return;
    
    const isEdit = selectedFlexpert.flexpert === true;
    
    setIsUpdatingFlexpert(true);
    // When creating new flexpert, send verifiedByAdmin: true
    // When editing existing flexpert, don't send verifiedByAdmin
    updateFlexpertStatus(selectedFlexpert.id, isEdit ? null : true, formData.salaryDetails, isEdit ? null : true);
  };

  const handleConsultantSelect = (event, newValue) => {
    if (newValue) {
      setSelectedConsultant(newValue);
      setSelectedFlexpert(newValue);
      setFlexpertModalOpen(true);
    }
  };

  const updateFlexpertStatus = (userId, flexExpert, salaryDetails, verifiedByAdmin) => {
    updateConsultantToFlexpert(userId, flexExpert, salaryDetails, verifiedByAdmin, null)
      .then((response) => {
        if (response.data && response.data.data && response.data.data.length > 0) {
          const updatedFlexpert = response.data.data[0];
          setFlexpert(prevFlexperts => 
            prevFlexperts.map(flex => 
              flex.id === userId 
                ? { 
                    ...flex, 
                    flexpert: updatedFlexpert.flexpert || updatedFlexpert.flexExpert, 
                    salaryDetails: updatedFlexpert.salaryDetails,
                    flexpertApprovedByAdmin: updatedFlexpert.flexpertApprovedByAdmin !== undefined 
                      ? updatedFlexpert.flexpertApprovedByAdmin 
                      : flex.flexpertApprovedByAdmin,
                    showInterview: updatedFlexpert.showInterview !== undefined 
                      ? updatedFlexpert.showInterview 
                      : flex.showInterview,
                    interviewVideo: updatedFlexpert.interviewVideo !== undefined 
                      ? updatedFlexpert.interviewVideo 
                      : flex.interviewVideo
                  } 
                : flex
            )
          );
        }
        
        setSnackbarMessage(
          flexExpert 
            ? "Successfully added consultant to Flexperts" 
            : "Successfully updated Flexpert"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setFlexpertModalOpen(false);
        setSelectedFlexpert(null);
        setSelectedConsultant(null);
        setConsultantSearch('');
        setConsultantOptions([]);
        setToggle(prev => !prev); 
      })
      .catch((err) => {
        console.error(err);
        setFlexpert(prevFlexperts => 
          prevFlexperts.map(flex => 
            flex.id === userId 
              ? { ...flex, flexpert: !flexExpert, ...(flexExpert ? { salaryDetails: undefined } : {}) } 
              : flex
          )
        );
        const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 
          (flexExpert ? "Failed to update Flexpert" : "Failed to remove FlexExpert status");
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => {
        setIsUpdatingFlexpert(false);
      });
  };

  const updateFlexpertVerification = (userId, verifiedByAdmin) => {
    updateConsultantToFlexpert(userId, null, null, verifiedByAdmin)
      .then((response) => {
        if (response.data && response.data.data && response.data.data.length > 0) {
          const updatedFlexpert = response.data.data[0];
          setFlexpert(prevFlexperts => 
            prevFlexperts.map(flex => 
              flex.id === userId 
                ? { ...flex, flexpertApprovedByAdmin: updatedFlexpert.flexpertApprovedByAdmin || verifiedByAdmin } 
                : flex
            )
          );
        }
        
        setSnackbarMessage(
          verifiedByAdmin 
            ? "Successfully verified Flexpert" 
            : "Successfully unverified Flexpert"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setToggle(prev => !prev); 
      })
      .catch((err) => {
        console.error(err);
        setFlexpert(prevFlexperts => 
          prevFlexperts.map(flex => 
            flex.id === userId 
              ? { ...flex, flexpertApprovedByAdmin: !verifiedByAdmin } 
              : flex
          )
        );
        const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 
          "Failed to update verification status";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  const updateShowInterview = (userId, showInterview) => {
    updateConsultantToFlexpert(userId, null, null, null, showInterview)
      .then((response) => {
        if (response.data && response.data.data && response.data.data.length > 0) {
          const updatedFlexpert = response.data.data[0];
          setFlexpert(prevFlexperts => 
            prevFlexperts.map(flex => 
              flex.id === userId 
                ? { ...flex, showInterview: updatedFlexpert.showInterview !== undefined ? updatedFlexpert.showInterview : showInterview } 
                : flex
            )
          );
        }
        
        setSnackbarMessage(
          showInterview 
            ? "Successfully enabled interview video display" 
            : "Successfully disabled interview video display"
        );
        setSnackbarSeverity("success");
        setSnackbarOpen(true);
        setToggle(prev => !prev); 
      })
      .catch((err) => {
        console.error(err);
        setFlexpert(prevFlexperts => 
          prevFlexperts.map(flex => 
            flex.id === userId 
              ? { ...flex, showInterview: !showInterview } 
              : flex
          )
        );
        const errorMessage = err?.response?.data?.error || err?.response?.data?.message || err?.message || 
          "Failed to update interview video display status";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      });
  };

  return (
    <div className="p-5 shadow-md bg-white">
      {flexpertModalOpen && selectedFlexpert && (
        <FlexpertPaymentModal
          open={flexpertModalOpen}
          onClose={() => {
            setFlexpertModalOpen(false);
            setSelectedFlexpert(null);
            setSelectedConsultant(null);
            setConsultantSearch('');
          }}
          onSubmit={handleFlexpertSubmit}
          consultantName={`${selectedFlexpert.firstName} ${selectedFlexpert.lastName}`}
          isLoading={isUpdatingFlexpert}
          initialSalaryDetails={selectedFlexpert.salaryDetails || []}
        />
      )}
      {confirmModalOpen && (
        <MuiModal 
          open={confirmModalOpen} 
          onCancel={() => {
            setConfirmModalOpen(false);
            setFlexpertToRemove(null);
          }} 
          title="Change Flexpert Status"
        >
          <div className="flex flex-col">
            <span className="text-base">
              Are you sure you want to change the status of this Flexpert back to Consultant?
            </span>
            {flexpertToRemove && (
              <span className="text-sm text-gray-600 mt-2">
                User: {flexpertToRemove.firstName} {flexpertToRemove.lastName} ({flexpertToRemove.username})
              </span>
            )}
            <div className="flex justify-center pt-4">
              <button 
                className={`hover:bg-red-400 bg-red-500 text-white py-2 px-4 rounded-lg mr-4`} 
                onClick={handleConfirmRemoveFlexpert}
              >
                Yes
              </button>
              <button 
                className={`text-white hover:bg-green-400 py-2 px-4 rounded-lg bg-[#2ebaa3]`} 
                onClick={() => {
                  setConfirmModalOpen(false);
                  setFlexpertToRemove(null);
                }}
              >
                No
              </button>
            </div>
          </div>
        </MuiModal>
      )}
      {videoModalOpen && selectedVideoUrl && (
        <MuiModal 
          open={videoModalOpen} 
          onCancel={() => {
            setVideoModalOpen(false);
            setSelectedVideoUrl(null);
          }} 
          title="Interview Video"
          maxWith="md"
        >
          <div className="flex flex-col items-center">
            <video 
              controls 
              style={{ width: '100%', maxHeight: '70vh' }}
              src={selectedVideoUrl}
            >
              Your browser does not support the video tag.
            </video>
          </div>
        </MuiModal>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
      <div className="grid grid-cols-6 gap-2 items-center">
        <h3
          className="col-span-3 md:col-span-2 text-lg uppercase tracking-wider font-bold"
          style={{ color: "#2EBAA3" }}
        >
          Flexperts
        </h3>
        <div className="col-span-3 md:col-span-4 flex justify-end items-center gap-2">
          {(isAdmin || userPermissions.includes('user-manager')) && (
            <Autocomplete
              value={selectedConsultant}
              onChange={handleConsultantSelect}
              inputValue={consultantSearch}
              onInputChange={(event, newInputValue) => {
                setConsultantSearch(newInputValue || "");
              }}
              options={consultantOptions || []}
              loading={consultantLoading}
              filterOptions={(x) => x}
              disablePortal
              getOptionLabel={(option) => {
                if (typeof option === 'string') return option;
                return `${option?.firstName || ''} ${option?.lastName || ''} (${option?.username || ''})`.trim() || 'Unknown';
              }}
              isOptionEqualToValue={(option, value) => {
                if (!option || !value) return false;
                return option?.id === value?.id;
              }}
              sx={{ width: 300 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Add Flexpert"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    className: "text-sm"
                  }}
                />
              )}
              noOptionsText={
                consultantLoading 
                  ? "Searching..."
                  : debouncedConsultantSearch.length >= 2
                    ? "No consultants found"
                    : "Type at least 2 characters to search"
              }
              renderOption={(props, option) => (
                <li {...props} key={option?.id || Math.random()}>
                  <div>
                    <div className="font-medium">{option?.firstName || ''} {option?.lastName || ''}</div>
                    <div className="text-xs text-gray-500">{option?.email || ''} - {option?.username || ''}</div>
                  </div>
                </li>
              )}
            />
          )}
          <input
            type="text"
            id="search"
            placeholder="Search"
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-1.5 form-control block max-w-xs text-base font-normal text-gray-700 bg-white bg-clip-padding border border-solid border-gray-300 rounded transition ease-in-out focus:text-gray-700 focus:bg-white focus:outline-none"
          />
        </div>
      </div>
      <br />
      <div class="overflow-x-auto relative">
        <table class="w-full text-sm text-left text-gray-500">
          <thead class="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" class="py-3 px-6 uppercase">
                Sr.
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Name
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Username
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Email ID
              </th>
              <th scope="col" class="py-3 px-6 uppercase">
                Country
              </th>
              <th scope="col" class="py-3 px-3 uppercase">
                Mobile Number
              </th>
              {(isAdmin || userPermissions.includes('user-manager')) && (
                <>
                  <th scope="col" class="py-3 px-6 uppercase">
                    Interview Video
                  </th>
                  <th scope="col" class="py-3 px-6 uppercase">
                    Show Video
                  </th>
                  <th scope="col" class="py-3 px-6 uppercase">
                    Is verified
                  </th>
                  <th scope="col" class="py-3 px-6 uppercase">
                    Flexpert
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {flexpert?.map((vend, index) => {
              return (
                <tr class="bg-white border-b " key={vend.id || index}>
                  <th
                    scope="row"
                    class="py-4 px-6 font-medium text-gray-900 whitespace-nowrap cursor-pointer"
                  >
                    {index + 1}
                  </th>
                  <td class="py-4 px-6">
                    {vend?.firstName + " " + vend?.lastName}
                  </td>
                  <td class="py-4 px-6">{vend?.username}</td>
                  <td class="py-4 px-6">{vend?.email}</td>
                  <td class="py-4 px-6">{vend?.country ?  vend?.country : countryData.find(c => c.countryCodes.includes(vend.countryCode))?.country || '--'}</td>
                  <td class="py-4 px-3">
                    {vend?.countryCode && vend?.phone
                      ? `${vend.countryCode.startsWith('+') ? '' : '+'}${vend.countryCode}${vend.phone}`
                      : vend?.phone
                      ? vend.phone
                      : '--'}
                  </td>
                  {(isAdmin || userPermissions.includes('user-manager')) && (
                    <>
                      <td class="py-4 px-6">
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<Play size={16} />}
                          onClick={() => handleViewVideo(vend?.interviewVideo)}
                          disabled={!vend?.interviewVideo}
                          sx={{ 
                            minWidth: 'auto',
                            padding: '4px 8px',
                            fontSize: '12px'
                          }}
                        >
                          View
                        </Button>
                      </td>
                      <td class="py-4 px-6">
                        {vend?.interviewVideo ? (
                          <Switch
                            checked={vend?.showInterview === true}
                            onChange={(e) => handleShowInterviewToggle(vend, e.target.checked)}
                            color="primary"
                            inputProps={{ "aria-label": "show-interview-toggle" }}
                          />
                        ) : (
                          <span className="text-gray-400">--</span>
                        )}
                      </td>
                      <td class="py-4 px-6">
                        <Switch
                          checked={vend?.flexpertApprovedByAdmin === true}
                          onChange={(e) => handleVerificationToggle(vend, e.target.checked)}
                          color="primary"
                          inputProps={{ "aria-label": "verification-toggle" }}
                        />
                      </td>
                      <td class="py-4 px-6">
                        <div className="flex items-center gap-2">
                          {vend?.flexpert === true && (
                            <>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleEditFlexpert(vend);
                                }}
                                sx={{ padding: "4px" }}
                                aria-label="edit-flexpert"
                              >
                                <Edit2 size={16} />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleDeleteFlexpert(vend);
                                }}
                                sx={{ padding: "4px", color: "error.main" }}
                                aria-label="delete-flexpert"
                              >
                                <Trash2 size={16} />
                              </IconButton>
                            </>
                          )}
                        </div>
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={flexpertTotal}
        page={flexpertPage}
        onPageChange={handleChangePage}
        rowsPerPage={flexpertLimit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[2, 5, 10, 25, 50, 100]}
      />
    </div>
  );
};

export default FlexpertUserList;

